import styled, {css, keyframes} from 'styled-components'
import {NavLink} from 'react-router-dom'
import {Form} from 'formik'
import {ArrowRight} from 'lucide-react'
import {Button} from '~/src/design-system/Primitives'
import CityPhoto from '~/src/assets/images/downtown-bg.jpeg'
import ParisPhoto from '~/src/assets/images/Paris.jpg'

const sceneTokens = css`
  --auth-bg: #04101f;
  --auth-surface: #081a30;
  --auth-ink: #eef4fb;
  --auth-ink-soft: #c9d6ea;
  --auth-ink-link: #b9cbe2;
  --auth-muted: #8fa2ba;
  --auth-faint: #5f7290;
  --auth-icon: #9fb3cf;
  --auth-icon-dim: #61738c;
  --auth-line: rgba(255, 255, 255, 0.09);
  --auth-line-strong: rgba(255, 255, 255, 0.16);
  --auth-field: rgba(255, 255, 255, 0.04);
  --auth-brand: #03a9fc;
  --auth-brand-light: #7dd3fc;
  --auth-brand-ice: #e0f5fe;
  --auth-brand-soft: rgba(3, 169, 252, 0.16);
  --auth-cta: #0277b6;
  --auth-cta-top: #0a8fd4;
  --auth-success: #22c55e;
  --auth-success-ice: #dcfce7;
  --auth-success-note: #6ee7b7;
  --auth-danger: #f87171;
  --auth-danger-ice: #fecaca;
  --auth-info-ice: #cdeefe;
  --auth-ease: cubic-bezier(0.22, 1, 0.36, 1);
  --auth-card-radius: 1.375rem;
  --auth-control-radius: var(--radius-md);
  --auth-button-height: 3.125rem;
  --auth-field-height: 3rem;
`

const AUTH_NARROW = '@media (max-width: 960px)'
const AUTH_PHONE = '@media (max-width: 560px)'
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'
const PAGE_WIDTH = 'min(72.5rem, calc(100% - var(--space-12)))'
const STACKED_GAP = '1.75rem'

const driftPhoto = keyframes`
  from { transform: scale(1); }
  to { transform: scale(1.05) translate(-1%, -0.6%); }
`
const driftA = keyframes`to { transform: translate(10vmax, 8vmax) scale(1.1); }`
const driftB = keyframes`to { transform: translate(-8vmax, 10vmax) scale(0.92); }`
const driftC = keyframes`to { transform: translate(-10vmax, -6vmax) scale(1.15); }`
const rise = keyframes`
  from { opacity: 0; transform: translateY(18px); filter: blur(6px); }
`
const viewIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
`
const beam = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
`
const nodePulse = keyframes`
  0%, 18%, 100% { border-color: var(--auth-line-strong); color: var(--auth-icon); box-shadow: none; }
  6%, 12% {
    border-color: rgba(3, 169, 252, 0.75);
    color: var(--white);
    box-shadow: 0 0 0 4px rgba(3, 169, 252, 0.14), 0 0 24px rgba(3, 169, 252, 0.35);
  }
`
const spin = keyframes`to { transform: rotate(360deg); }`
const draw = keyframes`to { stroke-dashoffset: 0; }`
const packet = keyframes`
  from { transform: translateX(-8px); }
  to { transform: translateX(7.75rem); }
`
const fill = keyframes`to { width: 100%; }`

const riseIn = css`
  animation: ${rise} 0.9s var(--auth-ease) both;
`
const viewInAnimation = css`
  animation: ${viewIn} 0.45s var(--auth-ease) both;
`

export const Scene = styled.div`
  ${sceneTokens}
  color-scheme: dark;
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  background: var(--auth-bg);
  color: var(--auth-ink);
  font-family: var(--font-sans);
  font-size: 0.9375rem;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
`

export const World = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`

export const Photo = styled.div<{$paris?: boolean}>`
  position: absolute;
  inset: -3%;
  background: url(${({$paris}) => ($paris ? ParisPhoto : CityPhoto)}) center 40% / cover no-repeat;
  animation: ${driftPhoto} 40s ease-in-out infinite alternate;
  will-change: transform;

  ${REDUCED_MOTION} {
    animation: none;
  }
`

const flatWash = css`
  background: linear-gradient(180deg, rgba(4, 16, 31, 0.9), rgba(4, 16, 31, 0.78));
`

export const Wash = styled.div<{$flat?: boolean}>`
  position: absolute;
  inset: 0;
  background: linear-gradient(
      100deg,
      rgba(4, 16, 31, 0.94) 0%,
      rgba(4, 16, 31, 0.84) 40%,
      rgba(4, 16, 31, 0.62) 100%
    ),
    linear-gradient(0deg, rgba(4, 16, 31, 0.7), transparent 45%);

  ${AUTH_NARROW} {
    ${flatWash}
  }

  ${({$flat}) => $flat && flatWash}
`

const auroraVariants = {
  a: css`
    width: 54vmax;
    height: 54vmax;
    left: -16vmax;
    top: -20vmax;
    background: radial-gradient(circle, var(--auth-cta) 0%, transparent 65%);
    opacity: 0.45;
    animation: ${driftA} 22s ease-in-out infinite alternate;
  `,
  b: css`
    width: 46vmax;
    height: 46vmax;
    right: -16vmax;
    top: 8vmax;
    background: radial-gradient(circle, var(--auth-brand) 0%, transparent 65%);
    opacity: 0.22;
    animation: ${driftB} 26s ease-in-out infinite alternate;
  `,
  c: css`
    width: 40vmax;
    height: 40vmax;
    left: 32vmax;
    bottom: -26vmax;
    background: radial-gradient(circle, #1e40af 0%, transparent 65%);
    opacity: 0.35;
    animation: ${driftC} 30s ease-in-out infinite alternate;
  `,
}

export const Aurora = styled.div<{$variant: keyof typeof auroraVariants}>`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  mix-blend-mode: screen;
  will-change: transform;
  ${({$variant}) => auroraVariants[$variant]}

  ${REDUCED_MOTION} {
    animation: none;
  }
`

export const Stage = styled.main<{$centered?: boolean}>`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(360px, 440px);
  gap: clamp(2.5rem, 7vw, 6.875rem);
  align-items: center;
  width: ${PAGE_WIDTH};
  min-height: 100vh;
  margin: 0 auto;
  padding: var(--space-12) 0 var(--space-24);

  ${AUTH_NARROW} {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    gap: ${STACKED_GAP};
    padding: var(--space-10) 0 var(--space-16);
  }

  ${AUTH_PHONE} {
    width: calc(100% - var(--space-8));
  }

  ${({$centered}) =>
    $centered &&
    css`
      grid-template-columns: minmax(0, 28.75rem);
      justify-content: center;
      align-content: center;
      gap: ${STACKED_GAP};
    `}
`

export const CenteredBrand = styled.div`
  display: flex;
  justify-content: center;
  ${riseIn}
`

export const Foot = styled.footer`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 1.125rem;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  width: ${PAGE_WIDTH};
  margin: 0 auto;
  color: var(--auth-faint);
  font-size: var(--fs-caption);

  ${AUTH_NARROW} {
    display: none;
  }
`

export const Iris = styled.div`
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background: var(--bg-page);
  clip-path: circle(0 at 50% 50%);
  pointer-events: none;
`

export const Intro = styled.section`
  ${riseIn}

  ${AUTH_NARROW} {
    display: grid;
    justify-items: center;
    text-align: center;

    & > :not(:first-child) {
      display: none;
    }
  }
`

export const Brand = styled.div`
  display: flex;
  width: max-content;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: 2.75rem;
  font-size: var(--fs-subtitle);
  font-weight: var(--fw-bold);
  letter-spacing: -0.01em;

  small {
    display: block;
    color: var(--auth-muted);
    font-size: 0.78125rem;
    font-weight: var(--fw-medium);
    letter-spacing: 0;
  }

  ${AUTH_NARROW} {
    margin-bottom: 0;
  }

  ${CenteredBrand} & {
    margin-bottom: 0;
  }
`

export const Headline = styled.h1`
  max-width: 37.5rem;
  margin: 0 0 var(--space-4);
  font-size: clamp(2.375rem, 4.3vw, 3.375rem);
  font-weight: var(--fw-bold);
  line-height: 1.03;
  letter-spacing: -0.04em;

  span {
    background: linear-gradient(100deg, var(--auth-brand-light), var(--auth-brand) 55%, #60a5fa);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`

export const Story = styled.p`
  max-width: 32.5rem;
  margin: 0 0 2.125rem;
  color: var(--auth-muted);
  font-size: 1.03125rem;
  line-height: 1.6;
`

export const Journey = styled.div`
  max-width: 35rem;
  margin-bottom: 1.875rem;
  padding: 1.375rem 1.375rem 1.125rem;
  border: 1px solid var(--auth-line);
  border-radius: 1.125rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.015));
  box-shadow: 0 30px 60px -30px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.06);
`

export const JourneyHead = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.125rem;
  color: var(--auth-muted);
  font-size: 0.71875rem;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;

  b {
    color: var(--auth-ink-soft);
    font-weight: var(--fw-semibold);
  }
`

export const Track = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, 1fr);

  &::before {
    content: '';
    position: absolute;
    top: 19px;
    left: 12.5%;
    right: 12.5%;
    height: 2px;
    border-radius: 2px;
    background: var(--auth-line-strong);
  }
`

export const Beam = styled.span`
  position: absolute;
  top: 19px;
  left: 12.5%;
  width: 75%;
  height: 2px;
  overflow: hidden;
  border-radius: 2px;

  &::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 34%;
    background: linear-gradient(90deg, transparent, var(--auth-brand), transparent);
    animation: ${beam} 4.8s var(--auth-ease) infinite;
  }

  ${REDUCED_MOTION} {
    &::after {
      animation: none;
      display: none;
    }
  }
`

export const Node = styled.div<{$index: number}>`
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  gap: 0.5625rem;
  text-align: center;

  i {
    display: grid;
    place-items: center;
    width: 2.5rem;
    height: 2.5rem;
    border: 1px solid var(--auth-line-strong);
    border-radius: var(--radius-md);
    background: var(--auth-surface);
    color: var(--auth-icon);
    font-size: var(--fs-caption);
    font-style: normal;
    font-weight: var(--fw-bold);
    animation: ${nodePulse} 4.8s var(--auth-ease) infinite;
    animation-delay: ${({$index}) => `${$index * 1.2 - 0.3}s`};

    ${REDUCED_MOTION} {
      animation: none;
    }
  }

  span {
    color: var(--auth-ink-soft);
    font-size: 0.78125rem;
    font-weight: var(--fw-semibold);
  }
`

export const Features = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-3);
  max-width: 35rem;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: grid;
    align-content: start;
    gap: var(--space-1);
    color: var(--auth-muted);
    font-size: 0.78125rem;
    line-height: 1.45;
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
    margin-bottom: var(--space-1);
    color: var(--auth-brand);
  }

  strong {
    color: var(--auth-ink);
    font-size: 0.8125rem;
    font-weight: var(--fw-semibold);
  }
`

export const AuthColumn = styled.div`
  display: grid;
  gap: 1.125rem;
  min-width: 0;

  ${AUTH_NARROW} {
    width: min(27.5rem, 100%);
  }
`

export const Card = styled.section`
  position: relative;
  padding: 2.125rem;
  border: 1px solid var(--auth-line-strong);
  border-radius: var(--auth-card-radius);
  background: linear-gradient(180deg, rgba(14, 28, 50, 0.84), rgba(7, 17, 33, 0.9));
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  backdrop-filter: blur(24px) saturate(1.4);
  box-shadow: 0 40px 100px -30px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  ${riseIn}
  animation-delay: 0.12s;
  view-transition-name: auth-card;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    z-index: -1;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      160deg,
      rgba(3, 169, 252, 0.5),
      transparent 35%,
      transparent 70%,
      rgba(96, 165, 250, 0.35)
    );
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  ${AUTH_NARROW} {
    width: 100%;
  }

  ${AUTH_PHONE} {
    padding: 1.625rem 1.375rem;
    border-radius: 1.125rem;
  }
`

export const View = styled.div`
  ${viewInAnimation}
`

export const CardLogo = styled.img`
  display: block;
  height: 1.875rem;
  width: auto;
  margin-bottom: 1.375rem;

  ${AUTH_NARROW} {
    display: none;
  }
`

export const CardTitle = styled.h2`
  margin: 0 0 0.375rem;
  color: var(--auth-ink);
  font-size: 1.5rem;
  font-weight: var(--fw-bold);
  letter-spacing: -0.025em;
`

export const Lede = styled.p`
  margin: 0 0 var(--space-6);
  color: var(--auth-muted);
  font-size: 0.90625rem;
`

const brandTint = css`
  background: linear-gradient(150deg, rgba(3, 169, 252, 0.3), rgba(3, 169, 252, 0.08));
  box-shadow: inset 0 0 0 1px rgba(125, 211, 252, 0.3);
`

export const Badge = styled.div<{$tone?: 'brand' | 'success'}>`
  display: grid;
  place-items: center;
  width: 3.25rem;
  height: 3.25rem;
  margin-bottom: 1.375rem;
  border-radius: 0.9375rem;
  ${brandTint}
  box-shadow: inset 0 0 0 1px rgba(125, 211, 252, 0.3), 0 12px 30px -10px rgba(3, 169, 252, 0.7);
  color: var(--auth-brand-ice);

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }

  ${({$tone}) =>
    $tone === 'success' &&
    css`
      background: linear-gradient(150deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.08));
      box-shadow: inset 0 0 0 1px rgba(134, 239, 172, 0.35),
        0 12px 30px -10px rgba(34, 197, 94, 0.7);
      color: var(--auth-success-ice);

      svg path {
        stroke-dasharray: 24;
        stroke-dashoffset: 24;
        animation: ${draw} 0.5s var(--auth-ease) 0.15s forwards;
      }

      ${REDUCED_MOTION} {
        svg path {
          stroke-dashoffset: 0;
          animation: none;
        }
      }
    `}
`

export const BannerBox = styled.div<{$tone: 'error' | 'info'}>`
  display: flex;
  gap: 0.625rem;
  margin: 0 0 1.125rem;
  padding: var(--space-3) 0.875rem;
  border: 1px solid rgba(248, 113, 113, 0.35);
  border-radius: var(--radius-md);
  background: rgba(248, 113, 113, 0.08);
  color: var(--auth-danger-ice);
  font-size: 0.84375rem;
  line-height: 1.45;
  ${viewInAnimation}

  svg {
    flex: none;
    width: 1.0625rem;
    height: 1.0625rem;
    margin-top: 1px;
    color: var(--auth-danger);
  }

  ${({$tone}) =>
    $tone === 'info' &&
    css`
      border-color: rgba(3, 169, 252, 0.35);
      background: rgba(3, 169, 252, 0.08);
      color: var(--auth-info-ice);

      svg {
        color: var(--auth-brand);
      }
    `}
`

const focusOutline = css`
  &:focus-visible {
    outline: 2px solid var(--auth-brand);
    outline-offset: 2px;
    box-shadow: none;
  }
`

const underlinedLink = css`
  border-radius: 6px;
  color: var(--auth-ink-link);
  font-weight: var(--fw-medium);
  text-decoration: underline;
  text-decoration-color: rgba(185, 203, 226, 0.4);
  text-underline-offset: 4px;
  transition: color 0.2s, text-decoration-color 0.2s;

  &:hover {
    color: var(--white);
    text-decoration-color: currentColor;
  }

  ${focusOutline}
`

const primaryButtonStyles = css`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  gap: 0.625rem;
  height: var(--auth-button-height);
  border: 0;
  border-radius: var(--auth-control-radius);
  background: linear-gradient(180deg, var(--auth-cta-top), var(--auth-cta));
  color: var(--white);
  font-size: 0.9375rem;
  font-weight: var(--fw-semibold);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 12px 30px -10px rgba(2, 119, 182, 0.95);
  transition: transform 0.25s var(--auth-ease), box-shadow 0.25s;

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
      105deg,
      transparent 35%,
      rgba(255, 255, 255, 0.25) 50%,
      transparent 65%
    );
    transform: translateX(-100%);
    transition: transform 0.8s var(--auth-ease);
  }

  &:hover:not(:disabled) {
    background: linear-gradient(180deg, var(--auth-cta-top), var(--auth-cta));
    transform: translateY(-1px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 16px 36px -10px rgba(2, 119, 182, 1);
  }

  &:hover:not(:disabled)::before {
    transform: translateX(100%);
  }

  &:hover:not(:disabled) .auth-arrow {
    transform: translateX(3px);
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }

  &:disabled {
    opacity: 1;
    cursor: default;
  }

  .auth-arrow {
    transition: transform 0.25s var(--auth-ease);
  }

  ${focusOutline}
`

export const AuthButton = styled(Button).attrs({variant: 'primary', size: 'lg', fullWidth: true})`
  ${primaryButtonStyles}
`

export const ButtonArrow = styled(ArrowRight).attrs({
  className: 'auth-arrow',
  'aria-hidden': true,
})``

export const TextButton = styled.button`
  ${underlinedLink}
  display: block;
  margin: var(--space-4) auto 0;
  padding: var(--space-1) 0.375rem;
  border: 0;
  background: none;
  font-family: var(--font-sans);
  font-size: 0.84375rem;
  cursor: pointer;
`

export const TextLink = styled(NavLink)`
  ${underlinedLink}
  padding: var(--space-1) 0.375rem;
  font-size: 0.8125rem;
`

export const ShieldNote = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4375rem;
  margin: 0.875rem 0 0;
  color: var(--auth-muted);
  font-size: 0.78125rem;
  text-align: center;

  svg {
    flex: none;
    width: 0.875rem;
    height: 0.875rem;
    color: var(--auth-success-note);
  }
`

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin: 1.625rem 0 1.25rem;
  color: var(--auth-muted);
  font-size: 0.71875rem;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--auth-line-strong);
  }
`

export const Below = styled.div`
  display: grid;
  justify-items: center;
  gap: var(--space-2);
  ${riseIn}
  animation-delay: 0.3s;
`

export const BelowAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5625rem;
  padding: 0.4375rem var(--space-3);
  border-radius: var(--radius-pill);
  color: var(--auth-muted);
  font-size: 0.8125rem;
  text-decoration: none;
  transition: color 0.2s, background-color 0.2s;

  strong {
    color: var(--auth-ink-soft);
    font-weight: var(--fw-semibold);
    transition: color 0.2s;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }

  .auth-go {
    width: 0.875rem;
    height: 0.875rem;
    opacity: 0.6;
    transition: transform 0.25s var(--auth-ease), opacity 0.2s;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--auth-ink-soft);
  }

  &:hover strong {
    color: var(--white);
  }

  &:hover .auth-go {
    opacity: 1;
    transform: translate(2px, -2px);
  }

  ${focusOutline}
`

export const BelowNote = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  margin: 0;
  color: var(--auth-muted);
  font-size: 0.8125rem;

  a {
    ${underlinedLink}
  }
`

export const EmailForm = styled(Form)`
  ${viewInAnimation}
`

export const Fields = styled.div`
  display: grid;
  gap: var(--space-6);
  margin-bottom: var(--space-6);

  label {
    margin-bottom: 0.4375rem;
    color: var(--auth-ink-soft);
    font-size: 0.8125rem;
    font-weight: var(--fw-semibold);
  }

  label + div > span:first-child {
    color: var(--auth-icon-dim);
  }

  label + div > span:first-child svg {
    width: 1.0625rem;
    height: 1.0625rem;
  }

  label + div > span:last-child button {
    color: var(--auth-muted);
  }

  label + div > span:last-child button:hover {
    color: var(--auth-ink);
  }
`

export const FormRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin: var(--space-1) 0 var(--space-5);
  font-size: 0.8125rem;

  a {
    margin: 0;
  }
`

export const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5625rem;
  margin: 0;
  color: var(--auth-ink-soft);
  font-weight: var(--fw-medium);
  cursor: pointer;

  input {
    width: 1.0625rem;
    height: 1.0625rem;
    margin: 0;
    accent-color: var(--auth-cta);
  }
`

export const Handoff = styled.div<{$direction: 'out' | 'back'}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: var(--space-1) 0 1.625rem;

  .auth-wire {
    position: relative;
    width: 7.5rem;
    height: 2px;
    margin: 0 0.625rem;
    overflow: hidden;
    border-radius: 2px;
    background: repeating-linear-gradient(
      90deg,
      var(--auth-line-strong) 0 6px,
      transparent 6px 11px
    );

    &::after {
      content: '';
      position: absolute;
      top: -2px;
      left: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--auth-brand);
      box-shadow: 0 0 12px 3px rgba(3, 169, 252, 0.7);
      animation: ${packet} 1.3s var(--auth-ease) infinite;
      animation-direction: ${({$direction}) => ($direction === 'back' ? 'reverse' : 'normal')};
    }

    ${REDUCED_MOTION} {
      &::after {
        animation: none;
        display: none;
      }
    }
  }
`

export const Tile = styled.span<{$idp?: boolean}>`
  display: grid;
  place-items: center;
  width: 3.75rem;
  height: 3.75rem;
  border: 1px solid var(--auth-line-strong);
  border-radius: 1.0625rem;
  background: var(--auth-surface);
  color: var(--auth-brand-ice);

  & > svg {
    width: 1.625rem;
    height: 1.625rem;
  }

  ${({$idp}) => $idp && brandTint}
`

export const Progress = styled.div<{$duration: number}>`
  height: 4px;
  margin: var(--space-1) 0 0.875rem;
  overflow: hidden;
  border-radius: 4px;
  background: var(--auth-line);

  i {
    display: block;
    width: 0;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, var(--auth-cta), var(--auth-brand));
    animation: ${fill} ${({$duration}) => $duration}ms var(--auth-ease) forwards;

    ${REDUCED_MOTION} {
      width: 100%;
      animation: none;
    }
  }
`

export const Steps = styled.ol`
  display: grid;
  gap: var(--space-1);
  margin: 0 0 0.375rem;
  padding: 0;
  list-style: none;
`

export const Step = styled.li<{$status: 'pending' | 'active' | 'done'}>`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 0.625rem var(--space-3);
  border-radius: 0.6875rem;
  color: #6f829c;
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  transition: color 0.3s, background-color 0.3s;

  .auth-dot {
    display: grid;
    place-items: center;
    width: 1.375rem;
    height: 1.375rem;
    flex: none;
    border: 1.5px solid var(--auth-line-strong);
    border-radius: 50%;
    transition: border-color 0.3s, background-color 0.3s;

    svg {
      width: 0.8125rem;
      height: 0.8125rem;
      opacity: 0;
    }
  }

  ${({$status}) =>
    $status === 'active' &&
    css`
      background: rgba(3, 169, 252, 0.08);
      color: var(--auth-ink);

      .auth-dot {
        border-color: transparent;
        border-top-color: var(--auth-brand);
        border-right-color: var(--auth-brand);
        animation: ${spin} 0.8s linear infinite;
      }
    `}

  ${({$status}) =>
    $status === 'done' &&
    css`
      color: var(--auth-ink-soft);

      .auth-dot {
        border-color: var(--auth-success);
        background: var(--auth-success);
        color: var(--auth-bg);
      }

      .auth-dot svg {
        opacity: 1;
        stroke-width: 3.2;
      }
    `}
`

export const CardContent = styled.div`
  --text-primary: var(--auth-ink);
  --text-secondary: var(--auth-muted);
  --text-placeholder: #5b6d86;
  --text-disabled: var(--auth-muted);
  --text-link: var(--auth-ink-link);
  --text-link-hover: var(--white);
  --bg-card: var(--auth-field);
  --bg-subtle: var(--auth-field);
  --bg-disabled: rgba(255, 255, 255, 0.08);
  --border-default: var(--auth-line-strong);
  --border-subtle: var(--auth-line);
  --border-strong: rgba(255, 255, 255, 0.26);
  --divider: var(--auth-line);
  --focus-border: rgba(3, 169, 252, 0.85);
  --focus-ring: 0 0 0 4px rgba(3, 169, 252, 0.2);
  --input-height-md: var(--auth-field-height);
  --input-height-lg: var(--auth-field-height);
  --neutral-900: var(--auth-ink);
  --neutral-800: var(--auth-ink-soft);
  --neutral-700: var(--auth-ink-soft);
  --neutral-600: var(--auth-muted);
  --danger: var(--auth-danger);
  --error-500: var(--auth-danger);
  color: var(--auth-ink);
`

export const CardFooterSlot = styled.div`
  margin-top: var(--space-6);
  color: var(--auth-muted);
  font-size: 0.8125rem;
  text-align: center;

  a {
    ${underlinedLink}
  }
`
